import { useCallback, useEffect, useState } from 'react';

import { useAccountState } from '@/contexts/account/account-context';
import {
  safeLocalStorageGet,
  safeLocalStorageRemove,
  safeLocalStorageSet,
} from '@/lib/helpers/browser/safe-storage';

const STORAGE_PREFIX = '@upstack/banner-dismissed';

interface UseDismissibleBannerOptions {
  bannerId: string;
  signal?: string | null;
  ttlMs?: number;
}

interface UseDismissibleBannerResult {
  isDismissed: boolean;
  dismiss: () => void;
  reset: () => void;
}

function buildStorageKey(bannerId: string, accountId: string | null, signal?: string | null): string {
  const account = accountId ?? 'global';
  const signalSegment = signal ?? '_';
  return `${STORAGE_PREFIX}:${bannerId}:${account}:${signalSegment}`;
}

function parseStoredTimestamp(raw: string | null): number | null {
  if (!raw) return null;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

export function useDismissibleBanner({
  bannerId,
  signal,
  ttlMs,
}: UseDismissibleBannerOptions): UseDismissibleBannerResult {
  const { accountId } = useAccountState();
  const storageKey = buildStorageKey(bannerId, accountId, signal);

  const [isDismissed, setIsDismissed] = useState<boolean>(() => {
    const stored = safeLocalStorageGet(storageKey);
    const dismissedAt = parseStoredTimestamp(stored);
    if (dismissedAt === null) return false;
    if (ttlMs && Date.now() - dismissedAt > ttlMs) return false;
    return true;
  });

  // Re-evaluate when the storage key changes (account switch or signal change).
  useEffect(() => {
    const stored = safeLocalStorageGet(storageKey);
    const dismissedAt = parseStoredTimestamp(stored);
    if (dismissedAt === null) {
      setIsDismissed(false);
      return;
    }
    if (ttlMs && Date.now() - dismissedAt > ttlMs) {
      safeLocalStorageRemove(storageKey);
      setIsDismissed(false);
      return;
    }
    setIsDismissed(true);
  }, [storageKey, ttlMs]);

  const dismiss = useCallback(() => {
    safeLocalStorageSet(storageKey, String(Date.now()));
    setIsDismissed(true);
  }, [storageKey]);

  const reset = useCallback(() => {
    safeLocalStorageRemove(storageKey);
    setIsDismissed(false);
  }, [storageKey]);

  return { isDismissed, dismiss, reset };
}
